// Route table shared by useRoutes (router/index.tsx), the prerender (entry-server.tsx) and
// preloadRoute (main.tsx). Pages are lazyPage() so a prerendered route's chunk can be loaded
// before hydrateRoot (see _seo-kit/pattern/PATTERN.md, "Preload the route chunk").
import type { ComponentType } from 'react';
import { matchRoutes, Navigate, type RouteObject } from 'react-router-dom';
import { lazyPage } from '../lib/lazyPage';

const HomePage = lazyPage(() => import('../pages/home/page'));
const ContactPage = lazyPage(() => import('../pages/contact/page'));
const CCMPage = lazyPage(() => import('../pages/programs/ccm/page'));
const RPMPage = lazyPage(() => import('../pages/programs/rpm/page'));
const BHIPage = lazyPage(() => import('../pages/programs/bhi/page'));
const TCMPage = lazyPage(() => import('../pages/programs/tcm/page'));
const PCMPage = lazyPage(() => import('../pages/programs/pcm/page'));
const NotFoundPage = lazyPage(() => import('../pages/NotFound'));

type Page = { path: string; Component: ComponentType & { preload?: () => Promise<unknown> } };

/** The catch-all "*" (404) must stay last. */
const pages: Page[] = [
  { path: '/', Component: HomePage },
  { path: '/contact', Component: ContactPage },
  { path: '/programs/ccm', Component: CCMPage },
  { path: '/programs/rpm', Component: RPMPage },
  { path: '/programs/bhi', Component: BHIPage },
  { path: '/programs/tcm', Component: TCMPage },
  { path: '/programs/pcm', Component: PCMPage },
  { path: '*', Component: NotFoundPage },
];

const routes: RouteObject[] = [
  ...pages.slice(0, -1).map(({ path, Component }) => ({ path, element: <Component /> })),
  // These used to render a second copy of the home page. They now point at its sections;
  // nginx answers them with a 301 too, and this covers client-side navigation.
  { path: '/services', element: <Navigate to={{ pathname: '/', hash: '#services' }} replace /> },
  { path: '/services/:serviceName', element: <Navigate to={{ pathname: '/', hash: '#services' }} replace /> },
  { path: '/programs', element: <Navigate to={{ pathname: '/', hash: '#programs' }} replace /> },
  ...pages.slice(-1).map(({ path, Component }) => ({ path, element: <Component /> })),
];

/** Loads the lazy page chunk matching `pathname`; main.tsx awaits it before hydrateRoot. */
export function preloadRoute(pathname: string): Promise<unknown> {
  const matches = matchRoutes(pages, pathname, __BASE_PATH__) ?? [];
  return Promise.all(matches.map((m) => (m.route as Page).Component.preload?.()));
}

export default routes;
